import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { SpinnerGap, Users, Phone, Envelope, Buildings } from '@phosphor-icons/react';
import { PersonLink } from '@/components/PersonLink';

const TABS = [
  { key: 'consultores', label: 'Consultores', endpoint: '/rede/consultores' },
  { key: 'promotores', label: 'Embaixadores', endpoint: '/rede/promotores' },
  { key: 'afiliados', label: 'Afiliados', endpoint: '/rede/afiliados' },
  { key: 'clientes', label: 'Clientes', endpoint: '/rede/clientes' },
];

export default function NetworkPage() {
  const [tab, setTab] = useState('consultores');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [counts, setCounts] = useState({ consultores: {}, promotores: {}, afiliados: {}, clientes: {} });

  useEffect(() => {
    api.post('/rede/countsAll').then(r => {
      const d = r.data || {};
      setCounts({
        consultores: d.consultores || {},
        promotores: d.promotores || {},
        afiliados: d.afiliados || {},
        clientes: d.clientes || {},
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const t = TABS.find(t => t.key === tab);
    api.post(t.endpoint).then(r => {
      setData(r.data?.data || (Array.isArray(r.data) ? r.data : []));
      setLoading(false);
    }).catch(() => { setData([]); setLoading(false); });
  }, [tab]);

  const filtered = data.filter(d =>
    !filter || (d.NomeCompleto || '').toLowerCase().includes(filter.toLowerCase()) ||
    (d.Email || '').toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div data-testid="network-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Rede</h1>
          <p className="text-sm text-[#6B7280] mt-1">Consultores, clientes, afiliados e embaixadores da sua rede</p>
        </div>
        <input placeholder="Filtrar por nome ou email..." value={filter} onChange={e => setFilter(e.target.value)} data-testid="rede-filter"
          className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm w-[240px] focus:ring-2 focus:ring-[#0F1829] outline-none" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {[
          { label: 'Consultores', c: counts.consultores, color: '#978D78' },
          { label: 'Embaixadores', c: counts.promotores, color: '#E65100' },
          { label: 'Afiliados', c: counts.afiliados, color: '#00C853' },
          { label: 'Clientes', c: counts.clientes, color: '#0F1829' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-[#E5E7EB] rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280]">{s.label}</p>
            <div className="flex items-baseline gap-3 mt-1">
              <div>
                <p className="text-xl font-bold font-mono" style={{ color: s.color }}>{s.c.diretos ?? '-'}</p>
                <p className="text-[9px] text-[#9CA3AF]">diretos</p>
              </div>
              <div>
                <p className="text-xl font-bold font-mono text-[#111827]">{s.c.rede != null ? new Intl.NumberFormat('pt-PT').format(s.c.rede) : '-'}</p>
                <p className="text-[9px] text-[#9CA3AF]">rede</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 mb-4">
        <a href="/rede/arvore" className="px-3 py-1.5 text-xs font-medium bg-[#0F1829] text-white rounded-md hover:bg-[#002480]">Ver Árvore Visual</a>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-[#E5E7EB]">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? 'border-[#0F1829] text-[#0F1829]' : 'border-transparent text-[#6B7280] hover:text-[#111827]'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>
      ) : (
        <>
          <p className="text-sm text-[#6B7280] mb-4">{filtered.length} registos</p>
          <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">ID</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Nome</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Email</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Empresa</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Telefone</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, i) => (
                  <tr key={item.ID_Cliente || i} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors">
                    <td className="px-4 py-3 text-sm font-mono text-[#6B7280]">{item.ID_Cliente}</td>
                    <td className="px-4 py-3 text-sm font-medium"><PersonLink id={item.ID_Cliente} name={item.NomeCompleto} /></td>
                    <td className="px-4 py-3 text-sm text-[#6B7280]">{item.Email || '-'}</td>
                    <td className="px-4 py-3 text-sm text-[#6B7280]">{item.Empresa || '-'}</td>
                    <td className="px-4 py-3 text-sm text-[#6B7280]">{item.Telefone || item.Descricao || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
          {filtered.length === 0 && <div className="text-center py-12 text-[#6B7280]">Sem registos</div>}
        </>
      )}
    </div>
  );
}
